import { Fragment } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  Timer as TimerIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  MenuBook as MenuBookIcon,
  Quiz as QuizIcon,
} from '@mui/icons-material';
import { styled } from '@mui/system';
import { useTranslation } from 'react-i18next';

// Componentes estilizados
const StyledContainer = styled(Container)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: '#f5f5f5',
  padding: theme.spacing(2),
}));

const StyledCard = styled(Card)(({ theme }) => ({
  maxWidth: 700,
  width: '100%',
  borderRadius: theme.spacing(2),
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
}));

const InstructionCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
}));

const PublicEvaluationInstructions = ({ evaluationData, onStartEvaluation, loading }) => {
  const { t } = useTranslation();

  // Formatear tiempo en minutos (MANTENER función existente)
  const formatTime = (minutes) => {
    if (minutes < 60) {
      return t('publicEvaluations.timeFormat.minutes', { minutes });
    }

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    const hoursLabel = hours === 1
      ? t('publicEvaluations.timeFormat.hour')
      : t('publicEvaluations.timeFormat.hours');

    return remainingMinutes > 0
      ? t('publicEvaluations.timeFormat.hoursAndMinutes', {
        hours,
        minutes: remainingMinutes,
        hoursLabel
      })
      : t('publicEvaluations.timeFormat.hoursOnly', { hours, hoursLabel });
  };

  // ✅ FUNCIÓN CORREGIDA para procesar competencias y calcular totales
  const processCompetencies = () => {
    console.log('evaluationData:', evaluationData);

    // ✅ CAMBIO: Ahora los datos están en capabilities
    const capabilities = evaluationData?.capabilities || [];
    console.log('capabilities:', capabilities);

    if (capabilities.length === 0) {
      return {
        competencies: [],
        totals: {
          readingTime: 0,
          questionTime: 0,
          totalTime: 0,
          questionCount: 0,
          competencyCount: 0
        }
      };
    }

    let totalReadingTime = 0;
    let totalQuestionTime = 0;
    let totalQuestions = 0;

    // Un caso, varias competencias (PLAN_CASOS_ESTUDIO.md): si la respuesta
    // viene agrupada, la lectura se cuenta UNA vez por caso (en la primera
    // competencia del grupo). Respuesta legacy → lectura por competencia, como antes.
    const groupedCaseStudies = (evaluationData?.caseStudies || []).filter((cs) => !cs.legacy);
    const isGrouped = groupedCaseStudies.length > 0;
    const caseByCapabilityId = {};
    groupedCaseStudies.forEach((cs) => {
      (cs.capabilityIds || []).forEach((capId, idx) => {
        caseByCapabilityId[capId] = { caseStudy: cs, isFirst: idx === 0 };
      });
    });

    const competencyInfo = capabilities.map(capability => {
      const group = isGrouped ? caseByCapabilityId[capability.capabilityId] : null;
      // ✅ CAMBIO: Usar los tiempos reales de cada capability.
      // Agrupado: la lectura se suma una vez por caso (primera competencia del grupo).
      const readingTime = group
        ? (group.isFirst ? (group.caseStudy.readingTime || 5) : 0)
        : (capability.readingTime || 5);
      const questionTime = capability.questionTime || 10;
      const questionCount = capability.questions?.length || 0;

      console.log(`Competencia ${capability.capabilityName}: readingTime=${readingTime}, questionTime=${questionTime}, questions=${questionCount}`);

      totalReadingTime += readingTime;
      totalQuestionTime += questionTime;
      totalQuestions += questionCount;

      return {
        capabilityId: capability.capabilityId,
        name: capability.capabilityName || 'Competencia',
        readingTime,
        questionTime,
        questionCount,
        totalTime: readingTime + questionTime
      };
    });

    // Agrupado: estructura por caso → competencias, en el orden en que se rinde
    const groups = isGrouped
      ? groupedCaseStudies.map((cs) => {
        const comps = (cs.capabilityIds || [])
          .map((capId) => competencyInfo.find((c) => c.capabilityId === capId))
          .filter(Boolean);
        const readingTime = cs.readingTime || 5;
        const questionTime = comps.reduce((t, c) => t + c.questionTime, 0);
        return {
          caseStudyId: cs.caseStudyId,
          title: cs.title || '',
          readingTime,
          questionTime,
          totalTime: readingTime + questionTime,
          questionCount: comps.reduce((t, c) => t + c.questionCount, 0),
          competencies: comps,
        };
      })
      : [];

    console.log('Totales calculados:', {
      totalReadingTime,
      totalQuestionTime,
      totalQuestions,
      competencyCount: capabilities.length
    });

    return {
      competencies: competencyInfo,
      isGrouped,
      groups,
      totals: {
        readingTime: totalReadingTime,
        questionTime: totalQuestionTime,
        totalTime: totalReadingTime + totalQuestionTime,
        questionCount: totalQuestions,
        competencyCount: capabilities.length
      }
    };
  };

  const { competencies, totals, isGrouped, groups } = processCompetencies();

  // Datos para compatibilidad con lógica existente
  const questionsCount = totals.questionCount;
  const timeLimit = evaluationData?.timeLimit || totals.totalTime; // Usar timeLimit como backup
  const applicantName = evaluationData?.applicantName || t('publicEvaluations.defaultApplicantName');
  const evaluationTitle = evaluationData?.evaluation?.title || t('publicEvaluations.defaultEvaluationTitle');
  const evaluationDescription = evaluationData?.evaluation?.description || '';

  return (
    <StyledContainer>
      <StyledCard>
        <CardContent sx={{ p: 4 }}>
          {/* Header (MANTENER IGUAL) */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Avatar sx={{
              mx: 'auto',
              mb: 2,
              bgcolor: 'primary.main',
              width: 80,
              height: 80
            }}>
              <AssignmentIcon sx={{ fontSize: 40 }} />
            </Avatar>
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
              {evaluationTitle}
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
              {t('publicEvaluations.helloApplicant', { name: applicantName })}
            </Typography>
            {evaluationDescription && (
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                {evaluationDescription}
              </Typography>
            )}
          </Box>

          {/* Información general MODIFICADA */}
          <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
              {t('publicEvaluations.evaluationInformation')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Chip
                icon={<AssignmentIcon />}
                label={t('publicEvaluations.competenciesLabel', { count: totals.competencyCount })}
                color="primary"
                variant="outlined"
              />
              <Chip
                icon={<QuizIcon />}
                label={t('publicEvaluations.questionsLabel', { count: questionsCount })}
                color="secondary"
                variant="outlined"
              />
              <Chip
                icon={<TimerIcon />}
                label={t('publicEvaluations.estimatedTimeLabel', { time: formatTime(totals.totalTime) })}
                color="warning"
                variant="outlined"
              />
            </Box>
          </Alert>

          {/* NUEVA sección: Estructura de la evaluación */}
          <InstructionCard>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                <InfoIcon sx={{ mr: 1, color: 'info.main' }} />
                {t('publicEvaluations.evaluationStructure')}
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {isGrouped
                  ? t('publicEvaluations.evaluationStructureDescriptionGrouped')
                  : t('publicEvaluations.evaluationStructureDescription')}
              </Typography>

              <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 420, '& td, & th': { px: { xs: 1, sm: 2 } } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{isGrouped ? t('publicEvaluations.caseStudyAndCompetencies') : t('publicEvaluations.competency')}</TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                          <MenuBookIcon fontSize="small" />
                          {t('publicEvaluations.reading')}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                          <QuizIcon fontSize="small" />
                          {t('publicEvaluations.questions')}
                        </Box>
                      </TableCell>
                      <TableCell align="center">{t('publicEvaluations.total')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Agrupado: una fila de caso (con su lectura) y debajo sus competencias */}
                    {isGrouped && groups.map((group, gIndex) => (
                      <Fragment key={group.caseStudyId}>
                        <TableRow sx={{ backgroundColor: 'action.hover' }}>
                          <TableCell component="th" scope="row">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <MenuBookIcon fontSize="small" color="info" />
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {t('publicEvaluations.caseStudyOf', { current: gIndex + 1, total: groups.length })}
                                {group.title ? `: ${group.title}` : ''}
                              </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ pl: 3.5 }}>
                              {t('publicEvaluations.caseStudyGroupHint', { count: group.competencies.length })}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>{formatTime(group.readingTime)}</Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" color="text.secondary">{formatTime(group.questionTime)}</Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>{formatTime(group.totalTime)}</Typography>
                          </TableCell>
                        </TableRow>
                        {group.competencies.map((comp) => (
                          <TableRow key={comp.capabilityId}>
                            <TableCell component="th" scope="row" sx={{ pl: 5 }}>
                              <Typography variant="body2">{comp.name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {comp.questionCount} {comp.questionCount === 1 ? t('publicEvaluations.question') : t('publicEvaluations.questions')}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="body2" color="text.disabled">—</Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="body2">{formatTime(comp.questionTime)}</Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="body2" color="text.disabled">—</Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </Fragment>
                    ))}

                    {/* Legacy: una fila por competencia, lectura + preguntas */}
                    {!isGrouped && competencies.map((comp, index) => (
                      <TableRow key={index}>
                        <TableCell component="th" scope="row">
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {comp.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {comp.questionCount} {comp.questionCount === 1 ? t('publicEvaluations.question') : t('publicEvaluations.questions')}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">
                            {formatTime(comp.readingTime)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">
                            {formatTime(comp.questionTime)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {formatTime(comp.totalTime)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ backgroundColor: 'grey.50' }}>
                      <TableCell component="th" scope="row">
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {t('publicEvaluations.totalTime')}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {formatTime(totals.readingTime)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {formatTime(totals.questionTime)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                          {formatTime(totals.totalTime)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </InstructionCard>

          {/* Instrucciones MODIFICADAS */}
          <InstructionCard>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                <CheckCircleIcon sx={{ mr: 1, color: 'success.main' }} />
                {t('publicEvaluations.importantInstructions')}
              </Typography>

              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="success" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('publicEvaluations.instructions.competencyBased')}
                    secondary={t('publicEvaluations.instructions.competencyBasedDesc')}
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="success" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('publicEvaluations.instructions.readingPhase')}
                    secondary={isGrouped
                      ? t('publicEvaluations.instructions.readingPhaseGroupedDesc')
                      : t('publicEvaluations.instructions.readingPhaseDesc')}
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="success" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('publicEvaluations.instructions.questionPhase')}
                    secondary={t('publicEvaluations.instructions.questionPhaseDesc')}
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="success" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('publicEvaluations.instructions.noGoingBack')}
                    secondary={isGrouped
                      ? t('publicEvaluations.instructions.noGoingBackGroupedDesc')
                      : t('publicEvaluations.instructions.noGoingBackDesc')}
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="success" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('publicEvaluations.instructions.answerHonestly')}
                    secondary={t('publicEvaluations.instructions.answerHonestlyDesc')}
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="success" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('publicEvaluations.instructions.timeBackup', { time: formatTime(timeLimit) })}
                    secondary={t('publicEvaluations.instructions.timeBackupDesc')}
                  />
                </ListItem>
              </List>
            </CardContent>
          </InstructionCard>

          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              {t('publicEvaluations.activityMonitoringDisclaimer')}
            </Typography>
          </Alert>

          {/* Recomendaciones MODIFICADAS */}
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
              {t('publicEvaluations.importantAboutTime')}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>{t('publicEvaluations.timeWillStartPerPhase')}</strong>
            </Typography>
            <Typography variant="body2">
              {t('publicEvaluations.recommendations.stableConnection')}<br />
              {t('publicEvaluations.recommendations.quietPlace')}<br />
              {t('publicEvaluations.recommendations.paperAndPen')}<br />
              {t('publicEvaluations.recommendations.dontCloseWindow')}<br />
              {t('publicEvaluations.recommendations.estimatedTime', { time: formatTime(totals.totalTime) })}<br />
              {t('publicEvaluations.recommendations.maxTime', { time: formatTime(timeLimit) })}
            </Typography>
          </Alert>

          <Divider sx={{ mb: 3 }} />

          {/* Botón de inicio (MANTENER IGUAL) */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t('publicEvaluations.whenReady')}
            </Typography>

            <Button
              variant="contained"
              size="large"
              onClick={onStartEvaluation}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <PlayArrowIcon />}
              sx={{
                py: 2,
                px: 4,
                fontSize: '1.2rem',
                fontWeight: 'bold',
                borderRadius: 2,
                textTransform: 'none'
              }}
            >
              {loading ? t('publicEvaluations.starting') : t('publicEvaluations.startEvaluation')}
            </Button>
          </Box>
        </CardContent>
      </StyledCard>
    </StyledContainer>
  );
};

export default PublicEvaluationInstructions;